import { toast } from '@bozic/ui/toast';
import axios, { type AxiosResponse } from 'axios';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getType, type ActionType } from 'typesafe-actions';

import { type AuthStateType, AuthState, getAuthState } from '@/shared/authentication';
import { isAuthorized } from '@/shared/authorization/isAuthorized';
import { getAgency, setAgency } from '@/shared/redux/slices/agency-slice';
import userAgreementNotAcceptedError from '@/shared/utils/userAgreementNotAcceptedError';

import { getErrorMessage } from '@/utils/invariant';

import SortDirection from '../../../enums/SortDirection';
import insuranceAPI from '../../../services/insuranceService';
import sortObjectByKey from '../../../utils/sortObjectByKey';
import { adminPageActions } from '../../adminPage/actions';
import { getAdminPageAgency } from '../../adminPage/selectors';
import { agencyLogoActions } from '../../agencyLogo/actions';
import { loadersActions } from '../../loaders/actions';
import { adminActions } from '../actions';
import { getInsuranceAgencies } from '../selectors';

import type { Agency } from '../../../types/Agency';

function* setActiveAgency(
  agencies: Agency[],
  cachedAgency: Agency | null,
  authState: AuthStateType
) {
  if (!cachedAgency) {
    yield put(adminPageActions.setAgency(agencies[0]));
    yield put(setAgency(agencies[0]));
  } else if (authState === AuthState.globalAdministrator) {
    yield put(adminPageActions.setAgency(cachedAgency));
    const agency = agencies.find(e => e.name === cachedAgency?.name);
    if (agency) {
      yield put(setAgency(agency));
    }
  }
}

// eslint-disable-next-line complexity -- legacy saga predates the limit; split on the next real touch
export function* getAllInsuranceAgencies() {
  try {
    yield put(loadersActions.adminAgencies.activate());
    const authState = (yield select(getAuthState)) as AuthStateType;
    const agencies = (yield call(insuranceAPI.getAgencies)) as AxiosResponse<Agency[]>;
    if (agencies.data.length > 0) {
      let cachedAgency: Agency | null = null;
      if (isAuthorized(authState, [AuthState.globalAdministrator])) {
        cachedAgency = JSON.parse(
          ((yield call([localStorage, 'getItem'], 'agency')) as string | null) ?? 'null'
        ) as Agency | null;
      }
      if (!cachedAgency) {
        yield put(adminPageActions.setAgency(agencies.data[0]));
        yield put(setAgency(agencies.data[0]));
      } else if (isAuthorized(authState, [AuthState.globalAdministrator])) {
        yield put(adminPageActions.setAgency(cachedAgency));
        const agency = agencies.data.find(e => e.name === cachedAgency?.name);
        if (agency) {
          yield put(setAgency(agency));
        }
      }
      yield* setActiveAgency(agencies.data, cachedAgency, authState);
    }
    yield put(adminActions.insuranceAgencies.addAllInsuranceAgencies(agencies.data));
    yield put(loadersActions.adminAgencies.disable());

    yield put(
      adminActions.insuranceAgencies.sort({ sort: 'fullName', direction: SortDirection.ASCENDING })
    );
  } catch (error) {
    const response = axios.isAxiosError(error) ? error.response : undefined;
    if (userAgreementNotAcceptedError(response)) {
      return;
    } else if (response?.status !== 404) {
      const responseBody: unknown = response?.data;
      const dataMessage =
        responseBody &&
        typeof responseBody === 'object' &&
        'message' in responseBody &&
        typeof responseBody.message === 'string'
          ? responseBody.message
          : undefined;
      toast.error(dataMessage ?? getErrorMessage(error), {
        position: 'top-right'
      });
    }
  }
}

export function* addNewInsuranceAgency(
  action: ActionType<typeof adminActions.insuranceAgencies.addInsuranceAgency>
) {
  try {
    yield put(loadersActions.spinner.activate());
    yield call(insuranceAPI.createAgency, action.payload);
    yield put(adminActions.insuranceAgencies.newInsuranceAgency(action.payload));
    yield put(loadersActions.spinner.disable());
    yield action.meta();
    yield toast.success('Insurance Agency Created');
  } catch (error) {
    yield put(loadersActions.spinner.disable());
    yield action.meta();
    toast.error(getErrorMessage(error), {
      position: 'top-right'
    });
  }
}

export function* removeInsuranceAgency(
  action: ActionType<typeof adminActions.insuranceAgencies.removeInsuranceAgency>
) {
  yield put(loadersActions.spinner.activate());
  const agency = (yield select(getAdminPageAgency)) as Agency;
  try {
    yield call(insuranceAPI.deleteAgency, action.payload);
    if (agency.name === action.payload) {
      yield put(adminPageActions.setAgency(null));
    }
    yield put(adminActions.insuranceAgencies.deleteInsuranceAgency(action.payload));
    yield toast.success('Insurance Agency Deleted');
  } catch (error) {
    const responseData: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
    toast.error(
      typeof responseData === 'string'
        ? responseData
        : error instanceof Error
          ? error.message
          : String(error),
      {
        position: 'top-right'
      }
    );
  } finally {
    yield put(loadersActions.spinner.disable());
    action.meta();
  }
}

export function* editInsuranceAgency(
  action: ActionType<typeof adminActions.insuranceAgencies.editInsuranceAgency>
) {
  try {
    yield put(loadersActions.spinner.activate());
    yield call(insuranceAPI.editAgency, action.meta.name, action.payload);
    yield put(
      adminActions.insuranceAgencies.updateInsuranceAgency(action.payload, action.meta.name)
    );

    const agency = (yield select(getAgency)) as Agency;
    if (agency.name === action.payload.name) {
      yield put(setAgency(action.payload));
    }
    yield put(loadersActions.spinner.disable());
    yield action.meta.cb();
    yield toast.success('Agency successfully edited.');
  } catch (error) {
    yield put(loadersActions.spinner.disable());
    action.meta.cb();
    toast.error(getErrorMessage(error), {
      position: 'top-right'
    });
  }
}

function* setAgencyToLocalStorageAndFetchLogo(
  action: ActionType<typeof adminPageActions.setAgency>
) {
  try {
    const authState = (yield select(getAuthState)) as AuthStateType;
    if (action.payload) {
      yield call([localStorage, 'setItem'], 'agency', JSON.stringify(action.payload));
      if (isAuthorized(authState, [AuthState.globalAdministrator])) {
        yield put(agencyLogoActions.getAdminAgencyLogo(action.payload.name));
      }
    }
  } catch (e) {
    console.error(e);
  }
}

export function* sortAgencies({
  payload: { sort, direction }
}: ActionType<typeof adminActions.insuranceAgencies.sort>) {
  const agencies = (yield select(getInsuranceAgencies)) as Agency[];
  const sortedAgencies = [...agencies].sort(sortObjectByKey(sort as keyof Agency, direction));

  yield put(adminActions.insuranceAgencies.addAllInsuranceAgencies(sortedAgencies));
}

// eslint-disable-next-line complexity -- legacy saga predates the limit; split on the next real touch
export function* editAgencyInsuranceProgram(
  action: ActionType<typeof adminActions.insuranceAgencies.editAgencyInsuranceProgram>
) {
  try {
    const selectedAgency = (yield select(getAgency)) as Agency;
    const agency: Agency = action.payload;
    if (selectedAgency.fullName === agency.fullName) {
      yield put(setAgency(agency));
    }
    yield call(insuranceAPI.editAgency, action.meta.name, action.payload);
    yield action.meta.cb();
    yield put(
      adminActions.insuranceAgencies.updateInsuranceAgency(action.payload, action.meta.name)
    );
    if (
      agency &&
      agency.insurancePrograms.includes('LGM') !== action.payload.insurancePrograms.includes('LGM')
    ) {
      yield toast.success(
        `Livestock Gross Margin ${
          action.payload.insurancePrograms.includes('LGM') ? 'enabled' : 'disabled'
        } for ${agency.fullName}.`
      );
    }
    if (
      agency &&
      agency.insurancePrograms.includes('DRP') !== action.payload.insurancePrograms.includes('DRP')
    ) {
      yield toast.success(
        `Dairy Revenue Protection ${
          action.payload.insurancePrograms.includes('DRP') ? 'enabled' : 'disabled'
        } for ${agency.fullName}.`
      );
    }
    if (
      agency &&
      agency.insurancePrograms.includes('LRP') !== action.payload.insurancePrograms.includes('LRP')
    ) {
      yield toast.success(
        `Livestock Risk Protection ${
          action.payload.insurancePrograms.includes('LRP') ? 'enabled' : 'disabled'
        } for ${agency.fullName}.`
      );
    }
  } catch (error) {
    toast.error(getErrorMessage(error), {
      position: 'top-right'
    });
  }
}

export function* insuranceAgenciesSagaWatcher() {
  yield all([
    takeLatest(
      getType(adminActions.insuranceAgencies.getAllInsuranceAgencies),
      getAllInsuranceAgencies
    ),
    takeLatest(getType(adminActions.insuranceAgencies.addInsuranceAgency), addNewInsuranceAgency),
    takeLatest(
      getType(adminActions.insuranceAgencies.removeInsuranceAgency),
      removeInsuranceAgency
    ),
    takeLatest(getType(adminActions.insuranceAgencies.editInsuranceAgency), editInsuranceAgency),
    takeLatest(
      getType(adminActions.insuranceAgencies.editAgencyInsuranceProgram),
      editAgencyInsuranceProgram
    ),
    takeLatest(getType(adminActions.insuranceAgencies.sort), sortAgencies),
    takeLatest(getType(adminPageActions.setAgency), setAgencyToLocalStorageAndFetchLogo)
  ]);
}
